/**
 * AppShell — the authenticated layout wrapper.
 *
 * Contains:
 *  - Sidebar with nav links (from route registry)
 *  - Header showing signed-in user (from /me via AINativeProvider)
 *  - Main content area where screen routes render via <Outlet>
 */

import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../providers/AINativeProvider.js";
import { NAV_ROUTES } from "../routes/registry.js";

// ─── Nav icons (inline SVG, no icon-font dependency) ─────────────────────────

function NavIcon({ icon }: { icon: string | undefined }) {
  switch (icon) {
    case "today":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" width="18" height="18">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      );
    case "projects":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" width="18" height="18">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      );
    case "deals":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" width="18" height="18">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
        </svg>
      );
    case "brain":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" width="18" height="18">
          <path d="M9.5 2C8.12 2 7 3.12 7 4.5v.17A4.5 4.5 0 003 9v.5A4.5 4.5 0 007 14v5.5A2.5 2.5 0 009.5 22h5a2.5 2.5 0 002.5-2.5V14a4.5 4.5 0 004-4.5V9a4.5 4.5 0 00-4-4.33V4.5C17 3.12 15.88 2 14.5 2h-5z" />
        </svg>
      );
    case "settings":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" width="18" height="18">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" width="18" height="18">
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
  }
}

// ─── Header ──────────────────────────────────────────────────────────────────

function Header() {
  const { user, signOut } = useAuth();

  return (
    <header className="shell-header" role="banner">
      <div className="shell-header__brand">
        <span className="shell-header__logo" aria-hidden="true">B</span>
        <span className="shell-header__wordmark">BuildOS</span>
      </div>

      <div className="shell-header__user">
        {user && (
          <>
            <span className="shell-header__user-name" aria-label={`Signed in as ${user.name ?? user.sub}`}>
              {user.name ?? user.sub}
            </span>
            {user.provider && (
              <span className="shell-header__user-provider">via {user.provider}</span>
            )}
          </>
        )}
        <button
          type="button"
          className="shell-header__signout"
          onClick={signOut}
          aria-label="Sign out"
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function Sidebar() {
  return (
    <nav className="shell-sidebar" aria-label="Main navigation">
      <ul className="shell-sidebar__list" role="list">
        {NAV_ROUTES.map((route) => (
          <li key={route.key} className="shell-sidebar__item">
            <NavLink
              to={route.path}
              end={route.path === "/"}
              className={({ isActive }) =>
                ["shell-sidebar__link", isActive ? "shell-sidebar__link--active" : ""].join(" ").trim()
              }
              aria-label={route.label}
            >
              <span className="shell-sidebar__icon">
                <NavIcon icon={route.icon} />
              </span>
              <span className="shell-sidebar__label">{route.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

// ─── AppShell ────────────────────────────────────────────────────────────────

export function AppShell() {
  return (
    <div className="shell-layout">
      <Header />
      <div className="shell-body">
        <Sidebar />
        <main className="shell-main" id="main-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
